import { colors } from 'app-v2/design-systems/theme'
import { COLOR } from 'app-v2/design-tokens'
import 'normalize.css'
import React from 'react'
import { isAndroid } from 'react-device-detect'
import Responsive from 'react-responsive'
import { colors as oldColors } from 'shared/assets/styles'
import styled, { css } from 'styled-components'
import { breakpoints, media } from './breakpoints'
import './style.css'

/* Css Shared Properties */
const button = css`
    border: none;
    -webkit-appearance: none;
    padding: 0;
    outline: 0;
    font-size: 0;
    background: none;
    cursor: pointer;
    text-align: center;
    &.focus {
        outline: 0;
    }
    [disabled] {
        opacity: 0.5;
    }
`

const boxShadow = css`
    box-shadow: 0 20px 30px -20px rgba(48, 49, 51, 0.2);
`

const flexBetween = css`
    display: flex;
    justify-content: space-between;
    align-items: center;
`

const classic = css`
    ${button};
    display: inline-block;
    font-family: 'Flame Regular';
    color: ${props => (props.color ? props.color : 'white')};
    background-color: ${props =>
        props.background ? props.background : oldColors.red};
    border: solid 1px ${props => (props.border ? props.border : oldColors.red)};
    border-radius: 10px;
    width: ${props => (props.width ? props.width : '')};

    &:hover {
        color: ${props => (props.color ? props.color : 'white')};
    }

    &:disabled {
        cursor: default;
        opacity: 0.8;
    }
    border-radius: 10px;
`

const placeholder = css`
    color: ${colors.greyBbqBrown};
`

const buttonSmall = css`
    ${classic};
    font-size: 14px;
    padding: 8px 18px;
`

const buttonMedium = css`
    ${classic};
    font-size: 16px;
    padding: 10px 24px;
`

const buttonLarge = css`
    ${classic};
    font-size: 18px;
    padding: 14px 33px;
`

/* Styled Elements */
const Button = styled.button`
    ${button};
`

const ButtonSmall = styled.button`
    ${buttonSmall};
`

const ButtonMedium = styled.button`
    ${buttonMedium};
`

const ButtonLarge = styled.button`
    ${buttonLarge};
`

const ButtonToggle = styled.span`
    ${classic};
    font-size: 16px;
    border: none;
    padding: 0 16px 0 0;
    font-family: 'Roboto Condensed';
    text-transform: none;
    margin-right: 10px;
    ${props => (props.marginBottom ? 'margin-bottom: 10px' : '')};
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    height: ${props => props.size}px;

    ${media.medium`
        margin-right: 20px;
        ${props => (props.marginBottom ? 'margin-bottom: 20px' : '')};
    `};
`

const ResponsiveMobile = (props: any) => (
    <Responsive {...props} maxWidth={breakpoints.medium - 1} />
)

const ResponsiveDesktop = (props: any) => (
    <Responsive {...props} minWidth={breakpoints.medium} />
)

const ResponsiveContainer = styled.div`
    margin: 0 20px;
    width: calc(100% - 40px);
    ${media.large`
        margin: 0 auto;
        width: 1280px;
    `};
`

const formInputDisplay = css`
    width: 100%;
    display: flex;
    justify-content: center;
    flex-direction: column;
    margin-top: 20px;
    font-family: 'Roboto Condensed';
    &.date {
        max-width: 200px;
    }
    &.restaurant,
    &.tpkModal {
        margin-top: 0;
    }
    &.dashboard {
        position: relative;
    }
`

const formInputStyle = css`
    -webkit-appearance: none;
    outline: 0;
    box-sizing: border-box;
    border-radius: 4px;
    padding: 5px 10px;
    margin-top: 8px;
    border: 1px solid ${colors.bbq30};
    &::-webkit-input-placeholder {
        ${placeholder};
    }
    &::-moz-placeholder {
        ${placeholder};
    }
    &:-ms-input-placeholder {
        ${placeholder};
    }
    &:-moz-placeholder {
        ${placeholder};
    }
    &.error {
        border-color: ${oldColors.red};
    }
    &.date,
    &.amount {
        max-width: 200px;
    }
    &.restaurant {
        margin-top: 0;
    }
    &.birthdate {
        max-width: 130px;
        ${media.medium`
            max-width: 100%;
        `};
    }
`

const InputForm = styled.div`
    ${formInputDisplay};
    input {
        ${formInputStyle};
        height: 40px;
        border-radius: 10px;
    }
`

const TextareaForm = styled.div`
    ${formInputDisplay};
    textarea {
        ${formInputStyle};
        height: 140px;
        resize: none;
    }
`

const SiteHeader = styled.header`
    position: ${props => (props.shouldStick ? 'fixed' : 'static')};
    top: 0;
    z-index: 1000;
    width: 100%;
    display: flex;
    flex-direction: column;
    background: ${props => {
        switch (props.background) {
            case 'plain':
                return COLOR.SECONDARY
            case 'gradient':
                return 'linear-gradient(-180deg, rgba(0,0,0,0.50) 0%, rgba(0,0,0,0.35) 51%, rgba(0,0,0,0.00) 100%)'
            case 'transparent':
                return 'transparent'
            default:
                return COLOR.SECONDARY
        }
    }};
    color: ${props => {
        switch (props.background) {
            case 'plain':
                return COLOR.PRIMARY
            case 'gradient':
                return COLOR.SECONDARY
            case 'transparent':
                return COLOR.SECONDARY
            default:
                return COLOR.PRIMARY
        }
    }};
    .hamburger,
    .map,
    .crown,
    .crown-path,
    .click-and-collect,
    .delivery {
        fill: ${props => {
            switch (props.background) {
                case 'plain':
                    return COLOR.PRIMARY
                case 'gradient':
                    return COLOR.SECONDARY
                case 'transparent':
                    return COLOR.SECONDARY
                default:
                    return COLOR.PRIMARY
            }
        }};
    }
    &.open {
        height: 100vh;
        background: ${oldColors.red2};
        color: white;
        .hamburger,
        .map,
        .crown,
        .crown-path,
        .click-and-collect,
        .delivery {
            fill: white;
        }
    }

    ${media.medium`
        border-bottom: ${props =>
            props.border ? `solid 1px ${colors.lightGrey}` : ''};
    `};
`

const MainNavigation = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    transition: all 200ms ease-in;
    ${SiteHeader}.hover &,
    ${SiteHeader}.sticky & {
        color: ${COLOR.PRIMARY};
        background: ${COLOR.SECONDARY};
        .hamburger,
        .map,
        .crown,
        .crown-path,
        .click-and-collect,
        .delivery {
            fill: ${COLOR.PRIMARY};
        }
    }
    ${props => isAndroid && props.showAppBanner && 'margin-top: 80px'};
`

const NavContainer = styled.nav`
    display: flex;
    justify-content: space-between;
    position: relative;
    min-height: 54px;
    ${media.medium`
        min-height: 90px;
    `};
`
const LogoContainer = styled.div`
    z-index: 1000;
    position: absolute;
    font-size: 0;
    left: 50%;
    bottom: -5px;
    transform: translateX(-50%);

    @media screen and (max-width: 1024px) {
        bottom: -10px;
    }
`

const classicModalMobileStyle = {
    overlay: {
        zIndex: 10000
    },
    content: {
        padding: 0,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        border: 'none',
        borderRadius: 0,
        height: '100vh',
        outline: 'none'
    }
}

const classicModalStyle = {
    overlay: {
        zIndex: 10000
    },
    content: {
        padding: 0,
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
        border: 'none',
        borderRadius: '10px',
        outline: 'none',
        overflowY: 'auto',
        overflowX: 'hidden',
        width: 'calc(100% - 40px)',
        height: 'auto',
        maxWidth: '500px',
        maxHeight: 'calc(100vh - 120px)'
    }
}

const centeredModalStyle = {
    overlay: {
        zIndex: 10000
    },
    content: {
        padding: 0,
        top: '90px',
        left: '10%',
        right: '10%',
        margin: 'auto',
        border: 'none',
        borderRadius: 0,
        outline: 'none',
        overflowX: 'hidden'
    }
}

const dropdownModalStyle = {
    overlay: {
        zIndex: 10000
    },
    content: {
        padding: 0,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        border: 'none',
        borderRadius: 0,
        outline: 'none',
        background: colors.lightMayo
    }
}

const style = {
    /* Shared Properties */
    button,
    buttonSmall,
    buttonMedium,
    buttonLarge,
    boxShadow,
    flexBetween,
    classicModalMobileStyle,
    classicModalStyle,
    centeredModalStyle,
    dropdownModalStyle
}

export {
    style,
    placeholder,
    formInputStyle,
    ResponsiveDesktop,
    ResponsiveMobile,
    Button,
    InputForm,
    TextareaForm,
    ButtonSmall,
    ButtonMedium,
    ButtonLarge,
    ButtonToggle,
    ResponsiveContainer,
    SiteHeader,
    MainNavigation,
    NavContainer,
    LogoContainer
}
